import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddCommasToAmount } from '../../../helper/Utils';
import DataTable from 'react-data-table-component';
import { ADD_CHALLAN_FAIL, ADD_CHALLAN_START } from '../../../RTK/Slices/ChallanSlice';
import { getAuthorities, getAuthoritieAccounts, createRoyaltyChallan } from '../../../helper/Api';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { validateUploadFileList } from '../../../helper/uploadValidation';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import Spinner from '../../../components/Tools/spinner';
import CustomBreadcrumbs from '../../../components/Tools/Breadcumbs';
import './AddRoyaltyChallan.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`royalty-col-header royalty-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="royalty-cell-center">{children}</div>
);

const actionIconBtnStyle = {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    background: 'transparent',
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getApprovalTypeLabel = (paymentType) => {
    if (paymentType === '1') return 'FULL PAYMENT';
    if (paymentType === '2') return 'PART PAYMENT';
    return '';
};

const AddRoyalty = () => {
    const [loading, setLoading] = useState(false);
    const { state } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [data] = useState(state?.data ? state.data : []);
    const [authorityAccounts, setAuthorityAccounts] = useState([]);
    const [authorities, setAuthorities] = useState([]);

    const [formData, setFormData] = useState({
        id: '',
        invoices: [],
        payment_order_no: '',
        amount: '',
        payment_order_date: '',
        doc_url: '',
        attachments: [],
        authority_id: '',
        authority_account_id: '',
    });

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 400,
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            name: <MultiLineHeader lines={['Invoice No', 'Invoice Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return row.invoice_no;
                const invoiceDate = row.invoice_date
                    ? moment(row.invoice_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.invoice_no || ''}</b></div>
                        {invoiceDate && <div className="text-muted fs-12px">{invoiceDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '130px',
            grow: 1.2,
        },
        {
            name: <MultiLineHeader lines={['Beneficiary', 'Details']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return (
                    <div>
                        <div>{row.company_name || ''}</div>
                        {row.pan_no && (
                            <div className="text-muted fs-12px">
                                <b>Pan No.:</b> {row.pan_no}
                            </div>
                        )}
                    </div>
                );
            },
            wrap: true,
            minWidth: '170px',
            grow: 2,
        },
        {
            name: <MultiLineHeader lines={['Approval', 'Type']} align="center" />,
            selector: (row) => (row.isFooter ? '' : getApprovalTypeLabel(row.payment_type)),
            wrap: true,
            minWidth: '110px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Voucher No', 'Voucher Date']} align="left" />,
            selector: (row) => {
                if (row.isFooter) return '';
                const voucherDate = row.voucher_date
                    ? moment(row.voucher_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY')
                    : '';
                return (
                    <div>
                        <div><b>{row.voucher_no || ''}</b></div>
                        {voucherDate && <div className="text-muted fs-12px">{voucherDate}</div>}
                    </div>
                );
            },
            wrap: true,
            minWidth: '150px',
            grow: 1.5,
        },
        {
            name: <MultiLineHeader lines={['Transaction', 'Date']} align="center" />,
            selector: (row) => {
                if (row.isFooter) return '';
                return row.transaction_date
                    ? moment(row.transaction_date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']).format('DD-MM-YYYY')
                    : '';
            },
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Taxable', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.taxable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Invoice', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.payable_amount)),
            minWidth: '105px',
            grow: 1,
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Sanctioned', 'Amount']} align="center" />,
            selector: (row) => (row.isFooter ? '' : formatAmount(row.sanction_amount)),
            minWidth: '115px',
            grow: 1,
            center: true,
        },
        {
            name: 'Royalty',
            selector: (row) => (
                <CenterCell>
                    {row.isFooter ? row.royalty_amount : formatAmount(row.royalty_amount)}
                </CenterCell>
            ),
            minWidth: '95px',
            grow: 1,
            center: true,
        },
        {
            name: 'Actions',
            selector: (row) => (
                row.isFooter ? null : (
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '6px', width: '100%' }}>
                        <button
                            type="button"
                            title="Download Attachment"
                            disabled={!row?.invoice}
                            onClick={() => handleDownload(row.invoice)}
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                cursor: row?.invoice ? 'pointer' : 'not-allowed',
                                opacity: row?.invoice ? 1 : 0.5,
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </button>
                    </div>
                )
            ),
            width: '100px',
            minWidth: '100px',
            grow: 0,
            center: true,
        },
    ];

    const calculateTotalAmount = (propertyName) => {
        let total = 0;
        data.forEach((row) => {
            const depositAmount = Number(parseFloat(row[propertyName]));
            if (!Number.isNaN(depositAmount)) {
                total += depositAmount;
            }
        });
        return total.toFixed(2);
    };

    const totalRoyaltyAmount = calculateTotalAmount('royalty_amount');

    const footerRow = {
        isFooter: true,
        invoice_no: <b>Total</b>,
        royalty_amount: <b>{AddCommasToAmount(totalRoyaltyAmount)}</b>,
    };

    const updatedData = [...data, footerRow];

    const fetchAuthorities = async () => {
        try {
            const response = await getAuthorities();
            if (response.data.status) {
                setAuthorities(response.data.result);
            }
        } catch (e) {
            // ignore
        }
    };

    const fetchAuthorityAccounts = async (id) => {
        try {
            const response = await getAuthoritieAccounts(id);
            if (response.data.status) {
                setAuthorityAccounts(response.data.result);
            }
        } catch (e) {
            setAuthorityAccounts([]);
        }
    };

    useEffect(() => {
        fetchAuthorities();
    }, []);

    const handleAttachmentChange = (event) => {
        const files = event.target.files;
        const validation = validateUploadFileList(files);
        if (!validation.ok) {
            toast.error(validation.message, {
                position: toast.POSITION.TOP_CENTER,
            });
            event.target.value = '';
            return;
        }
        setFormData((prev) => ({
            ...prev,
            attachments: files,
        }));
    };

    const handleChallanFormData = async (e) => {
        const { name, value } = e.target;

        if (name === 'authority_id') {
            setFormData((prev) => ({
                ...prev,
                authority_id: value,
                authority_account_id: '',
            }));
            if (value) {
                await fetchAuthorityAccounts(value);
            } else {
                setAuthorityAccounts([]);
            }
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validatePaymentForm = () => {
        const errors = [];

        if (!data.length) {
            errors.push('No invoices selected for payment.');
        }
        if (!String(formData.payment_order_no || '').trim()) {
            errors.push('Payment Order No is required.');
        }
        if (!formData.payment_order_date) {
            errors.push('Payment Order Date is required.');
        }
        if (!formData.authority_id) {
            errors.push('Authority is required.');
        }
        if (!formData.authority_account_id) {
            errors.push('Authority Account is required.');
        }
        if (!formData.attachments?.length) {
            errors.push('Upload Challan is required.');
        }
        if (!Number(totalRoyaltyAmount) || Number(totalRoyaltyAmount) <= 0) {
            errors.push('Challan Value must be greater than zero.');
        }

        return errors;
    };

    const handleSubmit = async () => {
        const validationErrors = validatePaymentForm();
        if (validationErrors.length) {
            toast.error(validationErrors[0], {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            const invoices = data.map((el) => el.id);
            const post_data = new FormData();

            for (let i = 0; i < formData.attachments.length; i += 1) {
                post_data.append('attachment[]', formData.attachments[i]);
            }

            post_data.append('invoices', invoices.join(','));
            post_data.append('payment_order_date', formData.payment_order_date);
            post_data.append('payment_order_no', String(formData.payment_order_no).trim());
            post_data.append('amount', totalRoyaltyAmount);
            post_data.append('authority_id', formData.authority_id);
            post_data.append('authority_account_id', formData.authority_account_id);

            dispatch(ADD_CHALLAN_START());
            const response = await createRoyaltyChallan(post_data);
            const addChallanResponseData = response.data;

            if (addChallanResponseData.status) {
                toast.success(addChallanResponseData.message || 'Royalty payment added successfully', {
                    position: toast.POSITION.TOP_CENTER,
                });
                navigate('/royalty');
            } else {
                toast.error(addChallanResponseData.message, {
                    position: toast.POSITION.TOP_CENTER,
                });
                dispatch(ADD_CHALLAN_FAIL(addChallanResponseData.message));
            }
        } catch (error) {
            dispatch(ADD_CHALLAN_FAIL('error getting while updating challan'));
            toast.error(error?.response?.data?.message || 'Failed to add payment', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const linksArray = [
        {
            href: '/royalty',
            label: 'Royalty',
        },
    ];

    return (
        <div className="p-3 add-royalty-challan-page">
            {loading && <Spinner />}

            <div className="row mb-3">
                <div className="col-md-6">
                    <h4 style={{ color: '#28326f', margin: 0 }}>Add Payment (Royalty)</h4>
                    <CustomBreadcrumbs linksArray={linksArray} title="Add Payment (Royalty)" />
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2" style={{ width: 'auto', float: 'right' }}>
                        <button type="button" className="submit_button" onClick={handleSubmit} disabled={loading}>
                            Add Payment
                        </button>
                    </div>
                </div>
            </div>

            <div className="card p-3">
                <div className="add-royalty-challan-datatable-root">
                    <DataTable
                        customStyles={customStyles}
                        className="dataTables_wrapper"
                        columns={columns}
                        data={updatedData}
                        fixedHeader
                        fixedHeaderScrollHeight="320px"
                        conditionalRowStyles={[
                            {
                                when: (row) => row.isFooter,
                                style: {
                                    backgroundColor: '#f8f9fc',
                                    fontWeight: 'bold',
                                },
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="card p-3 mt-3">
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="payment_order_no"
                                label="Payment Order No"
                                variant="outlined"
                                type="text"
                                name="payment_order_no"
                                value={formData.payment_order_no || ''}
                                onChange={handleChallanFormData}
                                placeholder="Enter Payment Order No"
                                required
                            />
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="payment_order_date"
                                label="Payment Order Date"
                                variant="outlined"
                                type="date"
                                name="payment_order_date"
                                value={formData.payment_order_date || ''}
                                onChange={handleChallanFormData}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <InputLabel id="authority-label">Authority</InputLabel>
                            <Select
                                labelId="authority-label"
                                id="authority_id"
                                name="authority_id"
                                value={formData.authority_id || ''}
                                label="Authority"
                                onChange={handleChallanFormData}
                                required
                            >
                                <MenuItem value="" disabled>
                                    Select Authority
                                </MenuItem>
                                {authorities.map((authority) => (
                                    <MenuItem value={authority.id} key={authority.id}>
                                        {authority.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <InputLabel id="authority-account-label">Authority Account</InputLabel>
                            <Select
                                labelId="authority-account-label"
                                id="authority_account_id"
                                name="authority_account_id"
                                value={formData.authority_account_id || ''}
                                label="Authority Account"
                                onChange={handleChallanFormData}
                                disabled={!formData.authority_id}
                                required
                            >
                                <MenuItem value="" disabled>
                                    Select Account
                                </MenuItem>
                                {authorityAccounts.map((account) => (
                                    <MenuItem value={account.id} key={account.id}>
                                        {account.bank_name} [{account.account_no}]
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="challan_value"
                                label="Challan Value"
                                variant="outlined"
                                type="text"
                                name="challan_value"
                                value={formatAmount(totalRoyaltyAmount)}
                                InputProps={{ readOnly: true }}
                                disabled
                                required
                            />
                        </FormControl>
                    </div>

                    <div className="col-md-6 mb-3">
                        <FormControl fullWidth>
                            <TextField
                                id="attachments"
                                label="Upload Challan"
                                type="file"
                                name="attachments"
                                onChange={handleAttachmentChange}
                                inputProps={{
                                    accept: '.pdf,.jpeg,.jpg,.png',
                                    multiple: true,
                                }}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddRoyalty;
